import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';

import {
  Configuration,
  FoodRequestOptions,
  Product,
  ProductPrint,
  ProductsSpecial,
  RecalcInfo,
  RecalcInfoRequest,
  ServerResponse
} from '../models';
import { AccountService } from './account.service';
import { AppConfig } from './app.config';
import { RestService } from './rest.service';

@Injectable()
export class ProductService {

  constructor(private restService: RestService,
    private accountService: AccountService
  ) {
    this.Config = AppConfig.config;
  }

  private Config: Configuration;

  private requestOptions = new FoodRequestOptions();
  private requestSpecial = new ProductsSpecial();
  private requestAlternativesSpecial = new ProductsSpecial();
  private specialProductsSubject = new BehaviorSubject<Product[]>(null);
  private specialLoading = false;
  private products: Product[];
  private specialProducts: Product[];
  private productsForPrint: ProductPrint[];
  private favorResponseSubject = new BehaviorSubject<String>(null);
  private excludedResponseSubject = new BehaviorSubject<String>(null);
  private isFilterChanged = false;
  private isSpecialFilterChanged = false;
  public specialProductsTotalPages: number;

  get(forDropdown: boolean = false): Promise<Product[]> {
    return new Promise<Product[]>(resolve => {

      if (forDropdown && (!this.requestOptions.Name || this.requestOptions.Name.length < 3)) {
        return resolve([]);
      }

      if (!this.isFilterChanged && this.products) {
        return resolve(this.products);
      }

      this.accountService.getCredentialsPromise().then(cred => {
        this.requestOptions.Id_session = cred.Id_Session;
        this.requestOptions.Id_app = this.Config.AppId;
        this.requestOptions.Id_user = cred.Id_User || undefined;
        this.requestOptions.ManualJSON = 0;
        this.requestOptions.PageSize = 20;
        this.requestOptions.Page = 1;
        this.requestOptions.Controller0 = 'User';
        this.requestOptions.EntityId0 = cred.Id_User || undefined;
        this.requestOptions.Controller1 = 'Product';
        this.requestOptions.Method = 'Print';
        this.requestOptions.ApiVersion = 'v1';
        this.requestOptions.IsExcluded = this.requestOptions.IsExcluded || 0;
        this.requestOptions.Sort = this.requestSpecial.Sort || 'FullName asc';

        this.restService.get(
          this.requestOptions).then(x => {
            if (x !== null) {
              const response: ServerResponse<Product> = x;
              this.products = response.Result.Resultset;
              resolve(this.products);
            }
          });
      });
    });
  }

  public getAlternativesSpecial(id_Recipe: number, id_Product: number): Promise<Product[]> {
    return new Promise<Product[]>(resolve => {

      this.accountService.getCredentialsPromise().then(cred => {
        const requestAlternativesSpecial = new FoodRequestOptions();

        requestAlternativesSpecial.Id_session = cred.Id_Session;
        requestAlternativesSpecial.Id_app = this.Config.AppId;
        requestAlternativesSpecial.ManualJSON = 0;
        requestAlternativesSpecial.Controller0 = 'Recipe';
        requestAlternativesSpecial.EntityId0 = id_Recipe;
        requestAlternativesSpecial.PageSize = 1000;
        requestAlternativesSpecial.Page = this.requestAlternativesSpecial.Page || 1;
        requestAlternativesSpecial.Controller1 = 'Product';
        requestAlternativesSpecial.EntityId1 = id_Product;
        requestAlternativesSpecial.Section = 'Alternative';
        requestAlternativesSpecial.Method = 'Print';
        requestAlternativesSpecial.ApiVersion = 'v1';

        this.restService.get(
          requestAlternativesSpecial).then(x => {
            if (x !== null) {
              const response: ServerResponse<Product> = x;
              response.Result.Resultset.forEach(alternative => {
                alternative.Id_Product = alternative.Id_ProductAlternative;
                alternative.Id_ProductAlternative = undefined;
              });

              resolve(response.Result.Resultset);
            }
          });
      });
    });
  }

  private refreshSpecial(): void {
    this.accountService.getCredentialsPromise().then(cred => {
      this.requestSpecial.Id_session = cred.Id_Session;
      this.requestSpecial.Id_app = this.Config.AppId;
      this.requestSpecial.ManualJSON = 0;
      this.requestSpecial.Controller0 = 'User';
      this.requestSpecial.EntityId0 = cred.Id_User;
      this.requestSpecial.PageSize = this.requestSpecial.PageSize || 1000;
      this.requestSpecial.Page = this.requestSpecial.Page || 1;
      this.requestSpecial.Controller1 = 'Product';
      this.requestSpecial.Method = 'Print';
      this.requestSpecial.ApiVersion = 'v1';
      this.requestSpecial.IsExcluded = this.requestSpecial.IsExcluded || 0;
      this.requestSpecial.Sort = this.requestSpecial.Sort || 'FullName asc';
      this.restService.get(
        this.requestSpecial).then(x => {
          if (x !== null) {
            const response: ServerResponse<Product> = x;
            this.specialProducts = response.Result.Resultset;
            // Только для первой страницы возвращается TotalPages.
            if (this.requestSpecial.Page === 1) {
              this.specialProductsTotalPages = response.Result.ReturnCode;
            }
            this.specialProductsSubject.next(this.specialProducts);
          }
          this.specialLoading = false;
        });
    });
  }

  public getSpecial(forceUpdate = false, skip: number = 0, rows: number = 10): Observable<Product[]> {
    this.setSpecialFilter('Page', Math.floor(skip / 10) + 1);
    this.setSpecialFilter('PageSize', rows);
    if (!this.specialProducts && !this.specialLoading || this.isSpecialFilterChanged || forceUpdate) {
      this.specialLoading = true;
      this.refreshSpecial();
      this.isSpecialFilterChanged = false;
    }

    return this.specialProductsSubject.asObservable();
  }

  public getPrintWeek(nweek: number): Promise<ProductPrint[]> {
    return new Promise<ProductPrint[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const requestPrintProducts = new ProductsSpecial();
        requestPrintProducts.Id_session = cred.Id_Session;
        requestPrintProducts.Id_app = this.Config.AppId;
        requestPrintProducts.ManualJSON = 0;
        requestPrintProducts.Controller0 = 'Plans';
        requestPrintProducts.EntityId0 = undefined;
        requestPrintProducts.Controller1 = 'PotDetail';
        requestPrintProducts.EntityId1 = nweek;
        requestPrintProducts.Method = 'Print';
        requestPrintProducts.ApiVersion = 'v1';
        this.restService.get(
          requestPrintProducts).then(x => {
            if (x !== null) {
              const response: ServerResponse<ProductPrint> = x;
              this.productsForPrint = response.Result.Resultset;
              resolve(this.productsForPrint);
            }
          });
      });
    });
  }

  public getRecalcValues(currentProductId: number): Promise<RecalcInfo[]> {
    return new Promise<RecalcInfo[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        let recalcInfoArray = new Array<RecalcInfo>();
        const currentRecalcValues = new RecalcInfoRequest;
        currentRecalcValues.Id_session = cred.Id_Session;
        currentRecalcValues.Id_app = this.Config.AppId;
        currentRecalcValues.Controller0 = 'Product';
        currentRecalcValues.EntityId0 = currentProductId;
        currentRecalcValues.Page = 1;
        currentRecalcValues.PageSize = 10;
        currentRecalcValues.Section = 'RecalcWeight';
        currentRecalcValues.ManualJSON = 0;
        currentRecalcValues.Method = 'Print';
        currentRecalcValues.ApiVersion = 'v1';
        this.restService.get(
          currentRecalcValues).then(x => {
            if (x !== null) {
              const response: ServerResponse<RecalcInfo> = x;
              recalcInfoArray = response.Result.Resultset;
              resolve(recalcInfoArray);
            }
          });
      });
    });
  }

  public setFavorite(productId: number, isFavorite): void {
    this.accountService.getCredentialsPromise().then(cred => {
      const favoriteExample = new FoodRequestOptions;
      favoriteExample.Id_session = cred.Id_Session;
      favoriteExample.Id_app = this.Config.AppId;
      favoriteExample.Controller0 = 'User';
      favoriteExample.EntityId0 = cred.Id_User;
      favoriteExample.Controller1 = 'Product';
      favoriteExample.EntityId1 = productId;
      favoriteExample.Method = isFavorite ? 'SetFavorit' : 'ClearFavorit';
      favoriteExample.ApiVersion = 'v1';
      if (isFavorite) {
        this.setExcuded(productId, false);
      }

      this.restService.post(favoriteExample).then(x => {
        if (x !== null) {
          this.favorResponseSubject.next(x.Result.Message);
        }
      });
    });
  }

  public setExcuded(productId: number, isExcluded): void {
    this.accountService.getCredentialsPromise().then(cred => {
      const excudedExample = new FoodRequestOptions;
      excudedExample.Id_session = cred.Id_Session;
      excudedExample.Id_app = this.Config.AppId;
      excudedExample.Controller0 = 'User';
      excudedExample.EntityId0 = cred.Id_User;
      excudedExample.Controller1 = 'Product';
      excudedExample.EntityId1 = productId;
      excudedExample.Method = isExcluded ? 'SetExcuded' : 'ClearExcuded';
      excudedExample.ApiVersion = 'v1';
      if (isExcluded) {
        this.setFavorite(productId, false);
      }

      this.restService.post(excudedExample).then(x => {
        if (x !== null) {
          this.excludedResponseSubject.next(x.Result.Message);
        }
      });
    });
  }

  public setFilter(name: string, value): void {
    if (this.requestOptions[name] !== value) {
      this.isFilterChanged = true;
      this.requestOptions[name] = value;
    }
  }

  public setFilterAll(name: string, value): void {
    if (this.requestOptions[name] !== value) {
      this.requestOptions[name] = value;
      this.isFilterChanged = true;
    }
  }

  public setSpecialFilter(name: string, value): void {
    if (this.requestSpecial[name] !== value) {
      this.isSpecialFilterChanged = true;
      this.requestSpecial[name] = value;
    }
  }

  public getProductById(id: number): Promise<Product> {
    return new Promise<Product>(resolve => {

      this.accountService.getCredentialsPromise().then(cred => {
        const request = new FoodRequestOptions();

        request.Id_session = cred.Id_Session;
        request.Id_app = this.Config.AppId;
        request.Id_user = cred.Id_User;
        request.ManualJSON = 0;
        request.PageSize = 20;
        request.Page = 1;
        request.Controller0 = 'User';
        request.EntityId0 = cred.Id_User;
        request.Controller1 = 'Product';
        request.EntityId1 = id;
        request.Method = 'Print';
        request.ApiVersion = 'v1';

        this.restService.get(
          request).then(x => {
            if (x !== null) {
              const response: ServerResponse<Product> = x;
              resolve(response.Result.Resultset[0]);
            }
          });
      });
    });
  }

  public getIngredientById(id: number): Promise<Product[]> {
    return new Promise<Product[]>(resolve => {

      this.accountService.getCredentialsPromise().then(cred => {
        const request = new FoodRequestOptions();

        request.Id_session = cred.Id_Session;
        request.Id_app = this.Config.AppId;
        request.Id_user = cred.Id_User;
        request.ManualJSON = 0;
        request.PageSize = 20;
        request.Page = 1;
        request.FullList = 1;
        request.id_product = id;
        request.Controller0 = 'User';
        request.EntityId0 = cred.Id_User;
        request.Controller1 = 'Product';
        request.Method = 'Print';
        request.ApiVersion = 'v1';

        this.restService.get(
          request).then(x => {
          if (x !== null) {
            const response: ServerResponse<Product> = x;
            resolve(response.Result.Resultset);
          }
        });
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/product.service.ts